/**
 * AuthContext - MSAL Authentication Wrapper
 * 
 * Provides a simplified authentication context that wraps MSAL functionality.
 * Handles user authentication state and provides access tokens for API calls.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  authMethods,
  apiTokenRequest,
  getOrSelectActiveAccount,
  setActiveAccountPersistent,
  saveAccountToHistory,
} from 'utils/msalConfig';
import logger from 'utils/logger';

/**
 * Diagnostic utility: Dump all MSAL-related localStorage keys and values
 * (Using localStorage since we switched from sessionStorage)
 */
// eslint-disable-next-line no-unused-vars
function dumpMsalLocalStorage() {
  const msalKeys = Object.keys(localStorage).filter(k => k.includes('msal') || k.includes('msal.'));
  return { keys: msalKeys };
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    logger.debug('AUTH-CONTEXT', 'useEffect triggered', {
        accountsCount: accounts?.length || 0,
        accounts: accounts?.map(a => ({
          username: a.username,
          localAccountId: a.localAccountId
        })) || [],
        inProgress,
        hasInstance: !!instance
      });
    
    // BEST PRACTICE: Trust useMsal hook - use only the accounts array
    // No need for cascading fallbacks or manual account queries
    // MsalProvider automatically manages accounts via handleRedirectPromise()
    
    // Update authentication state based on accounts array and inProgress
    if (accounts && accounts.length > 0 && inProgress === 'none') {
      // Use centralized active account helper to get the correct account
      const account = getOrSelectActiveAccount(instance);
      
      if (!account) {
        // Multiple accounts but none selected - user must choose
        if (accounts.length > 1) {
          logger.warn('AUTH-CONTEXT', 'Multiple accounts exist but no active account selected. User must choose.');
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        // No accounts at all
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      
      // Ensure this account is set as active and persisted
      setActiveAccountPersistent(instance, account);
      try {
        const all = instance.getAllAccounts() || [];
        for (const acc of all) {
          saveAccountToHistory(acc);
        }
      } catch (_) {}
      // Extract name - avoid "unknown" values
      let userName = account.name || '';
      // Filter out "unknown" as a name
      if (userName && userName.toLowerCase() === 'unknown') {
        userName = '';
      }
      // Fallback to username (email) if name is not available
      if (!userName) {
        userName = account.username || '';
      }
      
      const userInfo = {
        id: account.localAccountId,
        email: account.username || '',
        name: userName,
        tenantId: account.tenantId,
        homeAccountId: account.homeAccountId
      };
      
      logger.info('AUTH-CONTEXT', 'User authenticated', {
        email: userInfo.email,
        name: userInfo.name,
        id: userInfo.id
      });
      
      setUser(userInfo);
      setIsAuthenticated(true);
      setIsLoading(false);
    } else if (inProgress === 'none') {
      // No accounts and not in progress - user is not authenticated
      logger.info('AUTH-CONTEXT', 'User not authenticated', {
        inProgress,
        accountsCount: accounts?.length || 0,
        hasInstance: !!instance
      });
      
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    } else {
      // Still processing - keep loading state
      logger.debug('AUTH-CONTEXT', 'Still loading', { inProgress });
    }
  }, [accounts, inProgress, instance]);

  const getAccessToken = useCallback(async (scopes = []) => {
    if (!instance) {
      throw new Error('MSAL instance not available');
    }

    // Use centralized active account helper (deterministic selection)
    const account = getOrSelectActiveAccount(instance);
    if (!account) {
      throw new Error('No active account selected. Please choose an account.');
    }

    // Use OIDC scopes for Entra External ID, or API scope if provided
    const defaultScopes = scopes.length > 0 ? scopes : ['openid', 'profile', 'email'];
    
    // If API scope is needed, use apiTokenRequest helper
    const tokenRequestConfig = scopes.length > 0 && scopes.some(s => s.includes('api://')) 
      ? apiTokenRequest(scopes.find(s => s.includes('api://')))
      : { scopes: defaultScopes };

    try {
      const tokenRequest = {
        ...tokenRequestConfig,
        account: account
      };

      const response = await instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      // ✅ BEST PRACTICE: Check for InteractionRequiredAuthError specifically
      // CRITICAL: For no_tokens_found and InteractionRequiredAuthError, auto-logout instead of redirecting
      if (error instanceof InteractionRequiredAuthError) {
        const isNoTokensFound = error.errorCode === 'no_tokens_found' || 
                                error.message?.includes('no_tokens_found') ||
                                error.message?.includes('No refresh token found in the cache');
        
        if (isNoTokensFound) {
          logger.error('AUTH-CONTEXT', 'No tokens found - performing auto-logout instead of redirecting to Entra');
          try {
            const { performAutoLogout } = await import('../utils/authRecovery');
            performAutoLogout('no_tokens_found');
          } catch (importError) {
            logger.error('AUTH-CONTEXT', 'Failed to import performAutoLogout', { error: importError.message });
            // Fallback
            try {
              localStorage.clear();
              sessionStorage.clear();
              window.location.href = '/';
            } catch (e) {
              logger.error('AUTH-CONTEXT', 'Fallback logout failed', { error: e.message });
            }
          }
          return null;
        }
        
        // Check for silent iframe error (AADSTS160021)
        const isSilentIframeError = error.errorCode === 'interaction_required' &&
                                    (error.errorMessage?.includes('AADSTS160021') ||
                                     error.errorDescription?.includes('AADSTS160021'));
        
        // For all InteractionRequiredAuthError cases, auto-logout instead of redirecting
        logger.error('AUTH-CONTEXT', 'Interaction required - performing auto-logout instead of redirecting to Entra', {
          errorCode: error.errorCode,
          isSilentIframeError,
          errorMessage: error.message
        });
        
        try {
          const { performAutoLogout } = await import('../utils/authRecovery');
          performAutoLogout('interaction_required');
        } catch (importError) {
          logger.error('AUTH-CONTEXT', 'Failed to import performAutoLogout', { error: importError.message });
          // Fallback
          try {
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = '/';
          } catch (e) {
            logger.error('AUTH-CONTEXT', 'Fallback logout failed', { error: e.message });
          }
        }
        
        return null;
      } else {
        // For other errors, log and rethrow
        logger.error('AUTH-CONTEXT', 'Token acquisition failed', { error: error.message });
        throw error;
      }
    }
  }, [instance]);

  const login = useCallback(async () => {
    if (!instance) {
      throw new Error('MSAL instance not available');
    }

    // Use authMethods.login() from msalConfig.js for redirect flow consistency
    // This ensures we use the same loginRedirect logic as the rest of the app
    try {
      await authMethods.login(instance);
    } catch (error) {
      logger.error('AUTH-CONTEXT', 'Login failed', { error: error.message });
      throw error;
    }
  }, [instance]);

  const logout = useCallback(async () => {
    if (!instance) {
      throw new Error('MSAL instance not available');
    }

    try {
      // Use redirect logout to match app's authentication flow
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
    } catch (error) {
      logger.error('AUTH-CONTEXT', 'Logout failed', { error: error.message });
      throw error;
    }
  }, [instance]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    getAccessToken,
    login,
    logout,
    instance
  }), [user, isAuthenticated, isLoading, getAccessToken, login, logout, instance]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
